import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api/client';
import type { NotifyApi } from '../../components/notifications';
import { ClientDetailModal } from './ClientDetailModal';
import { AiCombo } from '../../components/AiCombo';
import { useModalKeys } from '../../hooks/useModalKeys';

// ─────────────────────────────────────────────────────────────────────────────
// GoLive "Project" dialog — opened by New and by the row Open command on the
// Client detail's Projects tab. Replaces the earlier 3-field mini form.
//
// Field set + layout ported from the legacy BO (Setting/Project.cs), left column
// then right column exactly as GoLive lays them out:
//   LEFT   Project ID* · Project Name · Client (read-only) · Project Location ·
//          Project Category · Project Code · To · CC
//   RIGHT  Project City · Project Overview · Project Source · Sample Retention ·
//          Comment · Retired By · Retired Date
//
// Legacy column names are preserved (§5.2): "Sample Retention" is
// DaysSampleKeeping, and To / CC are ReportingMailTo / ReportingMailCCTo — the
// SAME two columns the sample-receipt send path reads when resolving recipients,
// which is why they are the client's contact addresses rather than free text.
// ─────────────────────────────────────────────────────────────────────────────

interface Opt { id: string; name: string }
interface ClientRow { oid: string; name: string; address: string; phone: string }

interface ProjectForm {
  projectId: string; projectName: string; projectLocation: string;
  projectCategoryId: string; projectCode: string;
  reportingMailTo: string; reportingMailCCTo: string;
  projectCity: string; projectOverview: string; projectSource: string;
  daysSampleKeeping: string; comment: string;
  retiredById: string; retiredDate: string;
}
const EMPTY: ProjectForm = {
  projectId: '', projectName: '', projectLocation: '',
  projectCategoryId: '', projectCode: '', reportingMailTo: '', reportingMailCCTo: '',
  projectCity: '', projectOverview: '', projectSource: '',
  daysSampleKeeping: '', comment: '', retiredById: '', retiredDate: '',
};
type TextKey = 'projectId' | 'projectName' | 'projectCode' | 'projectCity'
  | 'projectOverview' | 'projectSource' | 'daysSampleKeeping';

export function ProjectDetailModal({ clientId, clientName, projectOid, onClose, onSaved, notify }: {
  clientId: string;
  clientName: string;
  projectOid: string | null;        // null = New
  onClose: () => void;
  onSaved: () => void;
  notify: NotifyApi;
}) {
  const [f, setF] = useState<ProjectForm>(EMPTY);
  const [loading, setLoading] = useState(!!projectOid);
  const [saving, setSaving] = useState(false);
  const [max, setMax] = useState(false);
  const [categories, setCategories] = useState<Opt[]>([]);
  const [employees, setEmployees] = useState<Opt[]>([]);
  // To = report contacts' Email; CC = report contacts' EmailCC (GoLive
  // Project.GetCheckedListBoxItems — CC binds EmailCC, not Email).
  const [toOpts, setToOpts] = useState<Opt[]>([]);
  const [ccOpts, setCcOpts] = useState<Opt[]>([]);
  // GoLive Client field is a 3-column grid picker (Client/Address/Phone).
  const [clientRows, setClientRows] = useState<ClientRow[]>([{ oid: clientId, name: clientName || 'N/A', address: '', phone: '' }]);
  // GoLive opens the Project dialog with Client = N/A (nothing pre-selected).
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [projCatOpen, setProjCatOpen] = useState(false);
  const [clientEditOpen, setClientEditOpen] = useState(false);
  // Bespoke popup keyboard (standing rule 14): Esc closes this hand-rolled overlay.
  const modalKeys = useModalKeys(true, { onClose });

  // Lookups: Client (every client), Project Category (dbo.ProjectCategory),
  // Retired By (Employee), To/CC (this client's contact e-mail addresses).
  const loadCategories = useCallback(() => api.get('/api/v1/clients/lookups/project-categories')
    .then(r => setCategories((r.data as { data?: Opt[] }).data ?? [])).catch(() => {}), []);
  useEffect(() => {
    void loadCategories();
    void api.get('/api/v1/lookups/employees')
      .then(r => setEmployees(((r.data as { data?: { oid: string; displayName: string }[] }).data ?? [])
        .map(e => ({ id: e.oid, name: e.displayName })))).catch(() => {});
    void api.get(`/api/v1/clients/${encodeURIComponent(clientId)}/contacts?type=0`)
      .then(r => {
        const cs = (r.data as { data?: { oid: string; email: string | null; emailCc: string | null }[] }).data ?? [];
        const uniq = (vals: (string | null)[]) => Array.from(new Set(vals.filter((v): v is string => !!v))).map(v => ({ id: v, name: v }));
        setToOpts(uniq(cs.map(c => c.email)));
        setCcOpts(uniq(cs.map(c => c.emailCc)));
      }).catch(() => {});
    void api.get('/api/v1/sample-receipt-notification/clients')
      .then(r => { const rows = (r.data as { data?: ClientRow[] }).data ?? []; if (rows.length) setClientRows(rows); }).catch(() => {});
  }, [clientId, loadCategories]);

  // Seed an edit from the FULL record (the grid row is only four columns).
  useEffect(() => {
    if (!projectOid) { setF({ ...EMPTY }); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    void api.get(`/api/v1/clients/${encodeURIComponent(clientId)}/projects/${projectOid}`)
      .then(r => {
        if (cancelled) return;
        const d = (r.data as { data?: Record<string, unknown> }).data ?? {};
        const s = (k: string) => String(d[k] ?? '');
        setF({
          projectId: s('projectId'), projectName: s('projectName'), projectLocation: s('projectLocation'),
          projectCategoryId: s('projectCategoryId'), projectCode: s('projectCode'),
          reportingMailTo: s('reportingMailTo'), reportingMailCCTo: s('reportingMailCCTo'),
          projectCity: s('projectCity'), projectOverview: s('projectOverview'),
          projectSource: s('projectSource'), daysSampleKeeping: s('daysSampleKeeping'),
          comment: s('comment'), retiredById: s('retiredById'), retiredDate: s('retiredDate').slice(0, 10),
        });
      })
      .catch(() => { if (!cancelled) notify.error('Failed to load the project.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [projectOid, clientId, notify]);

  const save = useCallback(async () => {
    if (!f.projectId.trim()) { notify.warning('Project ID is required.'); return; }
    setSaving(true);
    try {
      const body = { ...f, retiredDate: f.retiredDate || null, projectCategoryId: f.projectCategoryId || null,
                     retiredById: f.retiredById || null };
      // The project attaches to the SELECTED client (GoLive Client lookup); fall
      // back to the client being edited if the picker was cleared.
      const owner = selectedClientId || clientId;
      if (projectOid) await api.put(`/api/v1/clients/${encodeURIComponent(owner)}/projects/${projectOid}`, body);
      else await api.post(`/api/v1/clients/${encodeURIComponent(owner)}/projects`, body);
      notify.success('Saved successfully.');
      onSaved();
    } catch { notify.error('Failed to save the project.'); }
    finally { setSaving(false); }
  }, [f, projectOid, clientId, selectedClientId, notify, onSaved]);

  const input = (highlight: boolean): CSSProperties => ({
    width: '100%', padding: '4px 7px', border: '1px solid #cbd5e1', borderRadius: 5,
    boxSizing: 'border-box', fontSize: 12, background: highlight ? '#fffbe7' : '#fff',
  });
  const row = (label: string, control: ReactNode, req = false, top = false) => (
    <div style={{ display: 'grid', gridTemplateColumns: '116px 1fr', alignItems: top ? 'start' : 'center', gap: 8, marginBottom: 7 }}>
      <label style={{ fontSize: 11, textAlign: 'left', whiteSpace: 'nowrap', color: req ? '#d33' : '#4a4f58' }}>{label}:{req ? '*' : ''}</label>
      {control}
    </div>
  );
  const text = (key: TextKey, req = false): ReactNode => (
    <input type="text" value={f[key]} onChange={e => setF(p => ({ ...p, [key]: e.target.value }))}
           style={input(req && !f[key].trim())} />
  );
  // GoLive lookup: compact scrollable combobox · edit (New/Edit) · eraser
  // (clear). Uses the shared <Combo> (capped-height portal list) — matches the
  // ClientDetail lookups (e.g. the Retired By / Owner combobox), not a native
  // full-height <select>.
  const lookupCombo = (value: string, opts: Opt[], onChange: (v: string) => void, onEdit?: () => void): ReactNode => (
    <AiCombo value={value || null} options={opts.map(o => ({ value: o.id, label: o.name }))}
             onChange={v => onChange(v ?? '')} onNew={onEdit} newTitle="New / Edit" />
  );
  const area = (value: string, onChange: (v: string) => void, rows = 3): ReactNode => (
    <textarea rows={rows} value={value} onChange={e => onChange(e.target.value)}
              style={{ ...input(false), resize: 'vertical' }} />
  );

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1500,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} {...modalKeys}
           style={{ background: '#fff', borderRadius: 8, width: max ? '96vw' : 820, maxWidth: '96vw',
                    height: max ? '94vh' : undefined, maxHeight: '94vh', display: 'flex', flexDirection: 'column',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.3)', border: '2px solid var(--brand-accent, #e8670a)' }}>
        <style>{'.pdm-date.pdm-date-empty:not(:focus)::-webkit-datetime-edit{color:transparent}'}</style>
        <div style={{ padding: '10px 14px', fontWeight: 600, borderBottom: '1px solid #e5e7eb',
                      color: 'var(--ai-ink, #1f2430)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{f.projectName.trim() ? `${f.projectName} - Project` : 'Project'}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" title={max ? 'Restore' : 'Maximize'} onClick={() => setMax(m => !m)}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className={`ti ${max ? 'ti-minimize' : 'ti-maximize'}`} /></button>
            <button type="button" title="Close" onClick={onClose}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 18, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className="ti ti-x" /></button>
          </span>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading…</div>
        ) : (
          <div style={{ padding: 16, overflowY: 'auto' }}>
            <div style={{ display: 'flex', gap: 22 }}>
              <div style={{ flex: 1 }}>
                {row('Project ID', text('projectId', true), true)}
                {row('Project Name', text('projectName'))}
                {/* Client is GoLive's 3-column grid picker (Client/Address/Phone +
                    per-column filter + pager), defaulting to N/A. The ✎ opens the
                    full Client detail. Changing it targets the POST/PUT. */}
                {row('Client', <ClientGridPicker value={selectedClientId} rows={clientRows}
                       onChange={setSelectedClientId}
                       onEdit={() => setClientEditOpen(true)} />)}
                {row('Project Location', area(f.projectLocation, v => setF(p => ({ ...p, projectLocation: v }))), false, true)}
                {row('Project Category', lookupCombo(f.projectCategoryId, categories, v => setF(p => ({ ...p, projectCategoryId: v })), () => setProjCatOpen(true)))}
                {row('Project Code', text('projectCode'))}
                {/* To / CC feed dbo.Project.ReportingMailTo / ReportingMailCCTo — the
                    addresses the sample-receipt send path resolves against. */}
                {/* To binds report contacts' Email; CC binds their EmailCC
                    (GoLive Project.GetCheckedListBoxItems). */}
                {row('To', lookupCombo(f.reportingMailTo, toOpts, v => setF(p => ({ ...p, reportingMailTo: v }))))}
                {row('CC', lookupCombo(f.reportingMailCCTo, ccOpts, v => setF(p => ({ ...p, reportingMailCCTo: v }))))}
              </div>
              <div style={{ flex: 1 }}>
                {row('Project City', text('projectCity'))}
                {row('Project Overview', text('projectOverview'))}
                {row('Project Source', text('projectSource'))}
                {row('Sample Retention', text('daysSampleKeeping'))}
                {row('Comment', area(f.comment, v => setF(p => ({ ...p, comment: v }))), false, true)}
                {row('Retired By', lookupCombo(f.retiredById, employees, v => setF(p => ({ ...p, retiredById: v }))))}
                {row('Retired Date', <input type="date" className={f.retiredDate ? 'pdm-date' : 'pdm-date pdm-date-empty'}
                                            value={f.retiredDate} onChange={e => setF(p => ({ ...p, retiredDate: e.target.value }))}
                                            style={input(false)} />)}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 12, borderTop: '1px solid #e5e7eb' }}>
          <button type="button" className="ca-btn ca-btn-solid" disabled={saving || loading} onClick={() => void save()}>OK</button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={onClose}>Cancel</button>
        </div>
      </div>

      {/* Project Category ✎ → GoLive "Project Category" popup (dbo.ProjectCategory). */}
      {projCatOpen && (
        <ProjectCategoryModal
          onClose={() => setProjCatOpen(false)}
          onSaved={async (savedId) => { setProjCatOpen(false); await loadCategories(); if (savedId) setF(p => ({ ...p, projectCategoryId: savedId })); }}
          notify={notify} />
      )}

      {/* Client ✎ → the FULL Client detail popup (GoLive parity). Nested above
          this Project popup (zIndex 1700 > 1500). */}
      {clientEditOpen && (
        <ClientDetailModal clientId={selectedClientId || clientId} zIndex={1700}
          onClose={() => setClientEditOpen(false)}
          onSaved={() => setClientEditOpen(false)}
          notify={notify} />
      )}
    </div>
  );
}

// ── Project Category ✎ popup — GoLive "Project Category" (Category Name* /
//    Description) → dbo.ProjectCategory. ──────────────────────────────────────
function ProjectCategoryModal({ onClose, onSaved, notify }: {
  onClose: () => void; onSaved: (savedId: string) => void; notify: NotifyApi;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [nonCommercial, setNonCommercial] = useState(0);   // 0 = None, 1 = Yes
  const [saving, setSaving] = useState(false);
  const [max, setMax] = useState(false);
  const inp: CSSProperties = { width: '100%', padding: '4px 7px', border: '1px solid #cbd5e1', borderRadius: 5, boxSizing: 'border-box', fontSize: 12 };
  const save = useCallback(async () => {
    if (!name.trim()) { notify.warning('Category Name is required.'); return; }
    setSaving(true);
    try {
      const res = await api.post('/api/v1/clients/lookups/project-categories', { category: name, description, nonCommercial });
      notify.success('Saved successfully.');
      onSaved((res.data as { data?: { oid: string } }).data?.oid ?? '');
    } catch { notify.error('Failed to save the project category.'); }
    finally { setSaving(false); }
  }, [name, description, nonCommercial, notify, onSaved]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8, width: max ? '96vw' : 460, maxWidth: '96vw', height: max ? '94vh' : undefined, boxShadow: '0 10px 40px rgba(0,0,0,0.3)', border: '2px solid var(--brand-accent, #e8670a)' }}>
        <div style={{ padding: '10px 14px', fontWeight: 600, borderBottom: '1px solid #e5e7eb', color: 'var(--ai-ink, #1f2430)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Project Category</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" title={max ? 'Restore' : 'Maximize'} onClick={() => setMax(m => !m)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className={`ti ${max ? 'ti-minimize' : 'ti-maximize'}`} /></button>
            <button type="button" title="Close" onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, color: 'var(--brand-accent, #e8670a)', lineHeight: 1 }}><i className="ti ti-x" /></button>
          </span>
        </div>
        <div style={{ padding: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <label style={{ fontSize: 11, color: '#d33' }}>Category Name:*</label>
            <input style={{ ...inp, background: name.trim() ? '#fff' : '#fffbe7' }} value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'start', gap: 8, marginBottom: 8 }}>
            <label style={{ fontSize: 11 }}>Description:</label>
            <textarea rows={3} style={{ ...inp, resize: 'vertical' }} value={description} onChange={e => setDescription(e.target.value)} />
          </div>
          {/* Non Commercial — legacy ProjectCategory.CommercialType None(0)/Yes(1). */}
          <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 11 }}>Non Commercial:</label>
            <div style={{ display: 'flex', gap: 16, fontSize: 12 }}>
              <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" name="nc" checked={nonCommercial === 0} onChange={() => setNonCommercial(0)} /> None
              </label>
              <label style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
                <input type="radio" name="nc" checked={nonCommercial === 1} onChange={() => setNonCommercial(1)} /> Yes
              </label>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 12, borderTop: '1px solid #e5e7eb' }}>
          <button type="button" className="ca-btn ca-btn-solid" disabled={saving} onClick={() => void save()}>OK</button>
          <button type="button" className="ca-btn ca-btn-solid" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Client lookup in the Project dialog = GoLive's 3-column grid picker
//    (Client / Address / Phone) with a per-column filter row + pager, mirroring
//    the Sample Receipt Client picker. + edit (✎ → full Client detail) + eraser.
function ClientGridPicker({ value, rows, onChange, onEdit }: {
  value: string; rows: ClientRow[]; onChange: (oid: string) => void; onEdit?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const [fName, setFName] = useState(''); const [fAddr, setFAddr] = useState(''); const [fPhone, setFPhone] = useState('');
  const [page, setPage] = useState(0); const pageSize = 20;
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const selected = rows.find(r => r.oid === value);

  const place = () => { const r = btnRef.current?.getBoundingClientRect(); if (!r) return; setPos({ top: Math.min(r.bottom + 2, window.innerHeight - 340), left: r.left, width: Math.max(r.width, 560) }); };
  useLayoutEffect(() => { if (open) place(); }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { const t = e.target as Node; if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return; setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const filtered = rows.filter(r =>
    (!fName || r.name.toLowerCase().includes(fName.toLowerCase())) &&
    (!fAddr || (r.address || '').toLowerCase().includes(fAddr.toLowerCase())) &&
    (!fPhone || (r.phone || '').toLowerCase().includes(fPhone.toLowerCase())));
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const cur = Math.min(page, totalPages - 1);
  const pageRows = filtered.slice(cur * pageSize, (cur + 1) * pageSize);

  const th: CSSProperties = { textAlign: 'left', padding: '5px 8px', borderBottom: '1px solid #e5e7eb', color: '#4a4f58', fontSize: 11.5 };
  const fi: CSSProperties = { width: '100%', padding: '2px 5px', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 11, boxSizing: 'border-box' };
  const td: CSSProperties = { padding: '5px 8px', borderBottom: '1px solid #f0f0f0', fontSize: 12 };
  const pg = (disabled: boolean): CSSProperties => ({ border: '1px solid #e5e7eb', background: '#fff', borderRadius: 4, width: 22, height: 22, cursor: disabled ? 'not-allowed' : 'pointer', color: disabled ? '#c3c9d2' : '#4a4f58' });

  return (
    <div className="ai-lkbox aic" onKeyDown={(e) => {
      // Esc closes only this picker — stop it bubbling (React portal events bubble
      // to ancestors) to the modal's shared Esc handler (standing rule 14).
      if (open && e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); }
    }}>
      <button type="button" className="aic-trigger" ref={btnRef} onClick={() => setOpen(o => !o)}>
        <span className="aic-val">{selected?.name || 'N/A'}</span>
      </button>
      {onEdit && <button type="button" className="aic-new" title="New / Edit" onClick={onEdit}><i className="ti ti-pencil" /></button>}
      <button type="button" className="aic-caret" title="Show list" onClick={() => setOpen(o => !o)}><span className="aic-caret-tri" aria-hidden="true" /></button>
      <button type="button" className="aic-clr" title="Remove (N/A)" disabled={!value} onClick={() => onChange('')}><i className="ti ti-eraser" /></button>
      {open && pos && createPortal(
        <div ref={popRef} style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: 320, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, boxShadow: '0 6px 20px rgba(0,0,0,0.18)', zIndex: 2100, display: 'flex', flexDirection: 'column' }}>
          <div style={{ overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#fafbfc' }}><th style={th}>Client</th><th style={th}>Address</th><th style={th}>Phone</th></tr>
                <tr style={{ background: '#fafbfc' }}>
                  <th style={{ padding: '2px 6px' }}><input style={fi} value={fName} onChange={e => { setFName(e.target.value); setPage(0); }} /></th>
                  <th style={{ padding: '2px 6px' }}><input style={fi} value={fAddr} onChange={e => { setFAddr(e.target.value); setPage(0); }} /></th>
                  <th style={{ padding: '2px 6px' }}><input style={fi} value={fPhone} onChange={e => { setFPhone(e.target.value); setPage(0); }} /></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr><td colSpan={3} style={{ padding: 14, textAlign: 'center', color: '#6b7280', fontSize: 12 }}>No data to display</td></tr>
                ) : pageRows.map(r => (
                  <tr key={r.oid} onClick={() => { onChange(r.oid); setOpen(false); }}
                      style={{ cursor: 'pointer', background: r.oid === value ? '#fff3ea' : '#fff' }}>
                    <td style={td}>{r.name}</td><td style={td}>{r.address}</td><td style={td}>{r.phone}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', borderTop: '1px solid #e5e7eb', fontSize: 11.5, color: '#4a4f58', background: '#fafbfc' }}>
            <span>Page {cur + 1} of {totalPages} ({filtered.length} item{filtered.length === 1 ? '' : 's'})</span>
            <span style={{ display: 'flex', gap: 4 }}>
              <button type="button" disabled={cur <= 0} onClick={() => setPage(p => Math.max(0, p - 1))} style={pg(cur <= 0)}><i className="ti ti-chevron-left" /></button>
              <button type="button" disabled={cur >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} style={pg(cur >= totalPages - 1)}><i className="ti ti-chevron-right" /></button>
            </span>
          </div>
        </div>, document.body)}
    </div>
  );
}
